// The opportunity page's figures as its components take them: pure, so they can be tested without the page.
import type { PlanTone, RevenuePlanRow, RevenuePlanSection, TermTimelineStream } from '@olytic/foundation/cpq';

import { formatNumber } from '../objects/format';
import { bundleIds, orderLines } from '../quotes/lineTree';
import type { BuilderLine } from '../quotes/quotesApi';
import type { Delivery, OpportunitySummary, RevenuePlan, ScheduleLine } from './opportunityPageApi';

const STREAMS: ScheduleLine['stream'][] = ['Consulting', 'Studio', 'Software', 'Other'];

/** Each stream keeps one colour on the schedule card and in the plan grid. */
export const streamTone = (stream: string): PlanTone =>
  stream === 'Consulting' ? 1 : stream === 'Studio' ? 2 : stream === 'Software' ? 3 : 'other';

/** The schedule card's rows: one per revenue stream, each scheduled line a bar. */
export function scheduleStreams(lines: ScheduleLine[]): TermTimelineStream[] {
  return STREAMS.flatMap((stream) => {
    const inStream = lines.filter((l) => l.stream === stream);
    if (inStream.length === 0) return [];
    return [{
      key: stream,
      label: stream,
      tone: streamTone(stream),
      segments: inStream.map((l) => ({ key: l.line_id, label: l.name, start: l.start, end: l.end, amount: l.total })),
    }];
  });
}

export const hoursText = (n: number) => `${formatNumber(n, 1)}h`;

/** A row of figures by month, keyed as the grid wants them. */
function cellsOf(months: string[], amounts: number[], display?: (n: number) => string): RevenuePlanRow['cells'] {
  return Object.fromEntries(
    months.map((m, i) => {
      const value = amounts[i] ?? 0;
      // Nothing that month shows nothing, not $0.
      if (value === 0) return [m, { value: null, display: '' }];
      return [m, display ? { value, display: display(value) } : { value }];
    }),
  );
}

/** The grid's sections: the plan's rows, then revenue and cash, then the hours behind it. */
export function planSections(plan: RevenuePlan): RevenuePlanSection[] {
  const { months } = plan;
  return [
    {
      key: 'plan',
      title: 'Plan',
      rows: plan.rows.map((row) => ({
        key: row.key,
        label: row.label,
        kind: 'bar',
        ...(row.kind === 'component' ? { indent: true } : { tone: streamTone(row.stream) }),
        cells: cellsOf(months, row.amounts),
        total: compactDollars(row.total),
      })),
    },
    {
      key: 'cash',
      title: 'Revenue and Cash',
      rows: [
        { key: 'earned', label: 'Earned Revenue', kind: 'value', cells: cellsOf(months, plan.earned.amounts), total: compactDollars(plan.earned.total) },
        { key: 'invoiced', label: 'Invoiced', kind: 'value', cells: cellsOf(months, plan.invoiced.amounts), total: compactDollars(plan.invoiced.total) },
        { key: 'deferred', label: 'Deferred Revenue', kind: 'value', cells: cellsOf(months, plan.deferred.amounts) },
      ],
    },
    {
      key: 'delivery',
      title: 'Delivery',
      rows: [{ key: 'hours', label: 'Hours Needed', kind: 'value', cells: cellsOf(months, plan.hours.amounts, hoursText), total: hoursText(plan.hours.total) }],
    },
  ];
}

export interface DeliveryRow {
  id: string;
  label: string;
  depth: number;
  hours: number[];
  total: number;
  role?: boolean;
}

export const hours = (n: number | undefined) => (n ? formatNumber(n, 1) : '');

/** Role rows, each followed by its named people and what is still unstaffed, then the total. */
export function deliveryRows(delivery: Delivery, collapsed: Set<string>): DeliveryRow[] {
  const rows = delivery.roles.flatMap((role) => [
    { id: `role:${role.role_id}`, label: role.name, depth: 0, hours: role.hours, total: role.total, role: true },
    ...(collapsed.has(role.role_id)
      ? []
      : role.people.map((p) => ({
          id: `person:${role.role_id}:${p.app_user_id ?? 'unstaffed'}`,
          label: p.name,
          depth: 1,
          hours: p.hours,
          total: p.total,
        }))),
  ]);
  return [...rows, { id: 'total', label: 'Total', depth: 0, hours: delivery.totals.hours, total: delivery.totals.total }];
}

export interface LineRow {
  id: string;
  name: string;
  depth: number;
  bundle: boolean;
  total: number | null;
}

/**
 * The quote's lines as the rail shows them: each commercial line at what it is
 * scheduled to earn (its net without a schedule), a bundle's options under it
 * when opened, and the total.
 */
export function quoteLineRows(lines: BuilderLine[], summary: OpportunitySummary, open: Set<string>): LineRow[] {
  const scheduled = new Map(summary.schedule_lines.map((l) => [l.line_id, l.total]));
  const bundles = bundleIds(lines);
  const rows: LineRow[] = [];
  let total = 0;
  for (const line of orderLines(lines)) {
    if (line.parent_line_id) {
      if (!open.has(line.parent_line_id)) continue;
      rows.push({ id: line.id, name: line.name, depth: 1, bundle: false, total: line.scope || !line.included ? null : line.net });
      continue;
    }
    const value = scheduled.get(line.id) ?? (line.included ? line.net : 0);
    total += value;
    rows.push({ id: line.id, name: line.name, depth: 0, bundle: bundles.has(line.id), total: value });
  }
  rows.push({ id: 'total', name: 'Total', depth: 0, bundle: false, total: Math.round(total * 100) / 100 });
  return rows;
}

/** "2026-11" as "Nov 26" (the year only at January and the first month), for column heads. */
export function monthLabel(month: string, first = false): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const name = new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  return m === 1 || first ? `${name} ${String(y).slice(2)}` : name;
}

/** "2026-11" as "Nov 2026". */
export function monthLong(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** "$33.1k", "$1.2M", "$950": the grid's totals, as its cells show amounts. */
export function compactDollars(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${trim(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${sign}$${trim(abs / 1_000)}k`;
  return `${sign}$${Math.round(abs)}`;
}
const trim = (n: number) => (n >= 100 ? String(Math.round(n)) : n.toFixed(1).replace(/\.0$/, ''));
